import React from "react";

const defaultTableStyle = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: "0 8px",
  fontSize: "14px",
};

const defaultHeaderCellStyle = {
  background: "#b326aa",
  color: "#fff",
  fontWeight: "600",
  padding: "14px 16px",
  textAlign: "left",
  fontSize: "13px",
  letterSpacing: "0.5px",
};

const DataTable = ({
  columns,
  rows,
  loading = false,
  skeletonRowCount = 5,
  emptyMessage = "No data found",
  tableStyle = {},
  rowProps,
  loadingRow,
  rowKey,
}) => {
  const resolvedSkeletonRowCount =
    Number.isInteger(skeletonRowCount) && skeletonRowCount > 0
      ? skeletonRowCount
      : 5;

  return (
    <table style={{ ...defaultTableStyle, ...tableStyle }}>
      <thead>
        <tr style={{ height: "50px" }}>
          {columns.map((column, index) => {
            const isFirst = index === 0;
            const isLast = index === columns.length - 1;
            const resolvedHeaderTextAlign =
              column.headerStyle?.textAlign || column.cellStyle?.textAlign || "left";

            return (
              <th
                key={column.key || index}
                style={{
                  ...defaultHeaderCellStyle,
                  textAlign: resolvedHeaderTextAlign,
                  ...(isFirst ? { borderRadius: "8px 0 0 8px" } : {}),
                  ...(isLast ? { borderRadius: "0 8px 8px 0" } : {}),
                  ...(column.headerStyle || {}),
                }}
              >
                {column.label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          Array.from({ length: resolvedSkeletonRowCount }).map((_, idx) => {
            if (loadingRow) {
              const customLoadingRow = loadingRow(idx, columns);
              return React.isValidElement(customLoadingRow)
                ? React.cloneElement(customLoadingRow, {
                    key: customLoadingRow.key ?? `skeleton-${idx}`,
                  })
                : customLoadingRow;
            }

            return (
              <tr key={`skeleton-${idx}`}>
                {columns.map((column, colIdx) => (
                  <td
                    key={`${column.key || colIdx}-${idx}`}
                    style={{
                      padding: "14px 16px",
                      textAlign: column.cellStyle?.textAlign || "left",
                    }}
                  >
                    <div className="h-4 bg-gray-300 rounded w-24 animate-pulse" />
                  </td>
                ))}
              </tr>
            );
          })
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={{ padding: "40px 16px", textAlign: "center" }}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row, rowIndex) => {
            const dynamicProps = rowProps ? rowProps(row, rowIndex) : {};
            return (
              <tr key={rowKey ? rowKey(row, rowIndex) : rowIndex} {...dynamicProps}>
                {columns.map((column, colIndex) => (
                  <td
                    key={`${column.key || colIndex}-${rowIndex}`}
                    style={{
                      padding: "14px 16px",
                      ...(colIndex === 0 ? { borderRadius: "8px 0 0 8px" } : {}),
                      ...(colIndex === columns.length - 1 ? { borderRadius: "0 8px 8px 0" } : {}),
                      ...(column.cellStyle || {}),
                    }}
                  >
                    {column.render ? column.render(row, rowIndex) : row[column.key]}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};

export default DataTable;